import { mm, TOLERANCE, type Um } from './units.js';
import type { CabinetResult, HardwareItem, Issue, PanelPart, Vec3 } from './model.js';
import type { CabinetParams, FrontRow } from './params.js';
import { boxPenetration, computeEdgeCoverage, makePanel, partBox, type PanelSpec } from './geometry.js';

const MIN_PANEL: Um = mm(20);
const MIN_FRONT_HEIGHT: Um = mm(60);
const MAX_CABINET: Um = mm(4000);
/** Largest clearance recorded as edge proximity (policies decide what they accept). */
export const MAX_ENCLOSED_GAP: Um = mm(5);

type LengthKey = {
  [K in keyof CabinetParams]: CabinetParams[K] extends number ? K : never;
}[keyof CabinetParams];

const POSITIVE_LENGTHS: LengthKey[] = [
  'width',
  'height',
  'depth',
  'sideThickness',
  'bottomThickness',
  'topThickness',
  'shelfThickness',
  'dividerThickness',
  'frontThickness',
];

/** Validates parameters. Any `error` aborts geometry generation (no NaN / negative geometry is ever emitted). */
export function validateParams(cabinetId: string, p: CabinetParams): Issue[] {
  const issues: Issue[] = [];
  const err = (param: string, message: string) =>
    issues.push({ severity: 'error', code: 'param.invalid', message, cabinetId, param });

  for (const key of POSITIVE_LENGTHS) {
    const v = p[key];
    if (!Number.isInteger(v) || v <= 0) err(key, `Parametar ${key} mora biti pozitivan cijeli broj µm (dobijeno ${v}).`);
    else if (v > MAX_CABINET) err(key, `Parametar ${key} prelazi maksimum ${MAX_CABINET / 1000} mm.`);
  }
  for (const key of ['plinthHeight', 'legsHeight', 'plinthSetback', 'backThickness'] as const) {
    const v = p[key];
    if (!Number.isInteger(v) || v < 0) err(key, `Parametar ${key} ne smije biti negativan.`);
  }
  if (p.plinthHeight > 0 && p.legsHeight > 0) err('legsHeight', 'Sokla i nogice se ne mogu koristiti istovremeno.');
  if (!Number.isInteger(p.dividers) || p.dividers < 0 || p.dividers > 20) err('dividers', 'Broj pregrada mora biti 0–20.');
  if (!Number.isInteger(p.shelves.count) || p.shelves.count < 0 || p.shelves.count > 30)
    err('shelves.count', 'Broj polica mora biti 0–30.');
  if (p.back.mode !== 'none' && p.backThickness <= 0) err('backThickness', 'Leđa zahtijevaju debljinu veću od 0.');
  if (p.back.mode === 'groove' && p.back.grooveDepth <= p.back.clearance)
    err('back.grooveDepth', 'Dubina žlijeba mora biti veća od zazora leđa.');
  if (p.back.mode === 'groove' && p.back.grooveDepth >= p.sideThickness)
    err('back.grooveDepth', 'Dubina žlijeba mora biti manja od debljine stranice.');
  for (const [key, v] of [
    ['fronts.gapOuter', p.fronts.gapOuter],
    ['fronts.gapBetween', p.fronts.gapBetween],
    ['shelves.frontSetback', p.shelves.frontSetback],
    ['shelves.sideClearance', p.shelves.sideClearance],
  ] as const) {
    if (!Number.isInteger(v) || v < 0) err(key, `Parametar ${key} ne smije biti negativan.`);
  }
  if (issues.length) return issues;

  const innerW = p.width - 2 * p.sideThickness;
  if (innerW < MIN_PANEL) err('width', 'Širina je premala za zadate debljine stranica.');
  const innerH = p.height - p.bottomThickness - (p.topConstruction === 'none' ? 0 : p.topThickness);
  if (innerH < MIN_PANEL) err('height', 'Visina je premala za zadate debljine dna i vrha.');
  const clear = innerW - p.dividers * p.dividerThickness;
  if (clear / (p.dividers + 1) < MIN_PANEL) err('dividers', 'Previše pregrada za raspoloživu širinu.');
  if (p.back.mode === 'groove' && p.back.grooveInset + p.backThickness >= p.depth - MIN_PANEL)
    err('back.grooveInset', 'Položaj žlijeba leđa ne ostavlja dovoljno dubine.');
  return issues;
}

interface Frame {
  y0: Um; // carcass bottom
  innerX0: Um;
  innerX1: Um;
  innerY0: Um;
  innerY1: Um;
  innerZ0: Um; // front face of back panel (or carcass back)
  panelZ0: Um; // back of carcass panels (sides/bottom/top)
  frontZ: Um; // front plane of carcass
}

/**
 * Generates all panels of a parametric cabinet in cabinet-local coordinates.
 * Pure and deterministic: identical params ⇒ identical ids and dimensions.
 */
export function buildCabinet(cabinetId: string, p: CabinetParams): CabinetResult {
  const issues = validateParams(cabinetId, p);
  if (issues.some((i) => i.severity === 'error')) {
    return { cabinetId, parts: [], hardware: [], issues, bounds: { size: [0, 0, 0] } };
  }

  const specs: PanelSpec[] = [];
  const hardware: HardwareItem[] = [];
  const add = (s: Omit<PanelSpec, 'cabinetId'>) => specs.push({ cabinetId, ...s });

  const base = p.plinthHeight + p.legsHeight;
  const y0 = base;
  const H = p.height;
  const W = p.width;
  const sT = p.sideThickness;
  const panelZ0 = p.back.mode === 'overlay' ? p.backThickness : 0;
  const panelDepth = p.depth - panelZ0;
  const frontZ = p.depth;

  // --- Sides ---
  const sideY = p.bottomConstruction === 'under-sides' ? y0 + p.bottomThickness : y0;
  const sideTop = p.topConstruction === 'over-sides' ? y0 + H - p.topThickness : y0 + H;
  const sideH = sideTop - sideY;
  for (const [key, x, label] of [
    ['side-left', 0, 'Lijeva stranica'],
    ['side-right', W - sT, 'Desna stranica'],
  ] as const) {
    add({
      localKey: key,
      role: 'side',
      label,
      box: { min: [x, sideY, panelZ0], size: [sT, sideH, panelDepth] },
      lengthAxis: 'y',
      widthAxis: 'z',
      materialSlot: 'carcass',
    });
  }

  // --- Bottom ---
  const bottomX = p.bottomConstruction === 'between-sides' ? sT : 0;
  const bottomLen = p.bottomConstruction === 'between-sides' ? W - 2 * sT : W;
  add({
    localKey: 'bottom',
    role: 'bottom',
    label: 'Dno',
    box: { min: [bottomX, y0, panelZ0], size: [bottomLen, p.bottomThickness, panelDepth] },
    lengthAxis: 'x',
    widthAxis: 'z',
    materialSlot: 'carcass',
  });

  // --- Top ---
  if (p.topConstruction !== 'none') {
    const topX = p.topConstruction === 'between-sides' ? sT : 0;
    const topLen = p.topConstruction === 'between-sides' ? W - 2 * sT : W;
    add({
      localKey: 'top',
      role: 'top',
      label: 'Vrh',
      box: { min: [topX, y0 + H - p.topThickness, panelZ0], size: [topLen, p.topThickness, panelDepth] },
      lengthAxis: 'x',
      widthAxis: 'z',
      materialSlot: 'carcass',
    });
  }

  const innerY0 = y0 + p.bottomThickness;
  const innerY1 = p.topConstruction === 'none' ? y0 + H : y0 + H - p.topThickness;
  const innerX0 = sT;
  const innerX1 = W - sT;

  // --- Back ---
  let innerZ0 = panelZ0;
  if (p.back.mode === 'groove') {
    const g = p.back.grooveDepth - p.back.clearance;
    const bz = p.back.grooveInset;
    const topGroove = p.topConstruction === 'none' ? 0 : g;
    add({
      localKey: 'back',
      role: 'back',
      label: 'Leđa (u žlijebu)',
      box: {
        min: [innerX0 - g, innerY0 - g, bz],
        size: [innerX1 - innerX0 + 2 * g, innerY1 - innerY0 + g + topGroove, p.backThickness],
      },
      lengthAxis: 'y',
      widthAxis: 'x',
      materialSlot: 'back',
      grain: 'none',
    });
    innerZ0 = bz + p.backThickness;
  } else if (p.back.mode === 'overlay') {
    add({
      localKey: 'back',
      role: 'back',
      label: 'Leđa (nalijepljena)',
      box: { min: [0, y0, 0], size: [W, H, p.backThickness] },
      lengthAxis: 'y',
      widthAxis: 'x',
      materialSlot: 'back',
      grain: 'none',
    });
  }

  const frame: Frame = { y0, innerX0, innerX1, innerY0, innerY1, innerZ0, panelZ0, frontZ };
  const insetDepth = p.fronts.mounting === 'inset' && p.fronts.rows.length > 0 ? p.frontThickness : 0;

  // --- Dividers & compartments ---
  const compartments = splitCompartments(frame, p);
  for (let i = 0; i < p.dividers; i++) {
    const left = compartments[i]!;
    add({
      localKey: `divider-${i + 1}`,
      role: 'divider',
      label: `Pregrada ${i + 1}`,
      box: {
        min: [left.x1, innerY0, innerZ0],
        size: [p.dividerThickness, innerY1 - innerY0, frontZ - insetDepth - innerZ0],
      },
      lengthAxis: 'y',
      widthAxis: 'z',
      materialSlot: 'carcass',
    });
  }

  // --- Shelves ---
  const hasDrawers = p.fronts.rows.some((r) => r.kind === 'drawer');
  if (p.shelves.count > 0 && !hasDrawers) {
    const k = p.shelves.count;
    const innerH = innerY1 - innerY0;
    const clearH = innerH - k * p.shelfThickness;
    if (clearH / (k + 1) < MIN_PANEL) {
      issues.push({ severity: 'error', code: 'shelves.too-many', message: 'Previše polica za visinu.', cabinetId, param: 'shelves.count' });
    } else {
      const shelfDepth = frontZ - insetDepth - p.shelves.frontSetback - innerZ0;
      compartments.forEach((c, ci) => {
        for (let s = 0; s < k; s++) {
          // Equal clear spacing; cumulative rounding keeps total exact.
          const y = innerY0 + Math.round((clearH * (s + 1)) / (k + 1)) + s * p.shelfThickness;
          add({
            localKey: `shelf-${ci + 1}-${s + 1}`,
            role: 'shelf',
            label: compartments.length > 1 ? `Polica ${ci + 1}.${s + 1}` : `Polica ${s + 1}`,
            box: {
              min: [c.x0 + p.shelves.sideClearance, y, innerZ0],
              size: [c.x1 - c.x0 - 2 * p.shelves.sideClearance, p.shelfThickness, shelfDepth],
            },
            lengthAxis: 'x',
            widthAxis: 'z',
            materialSlot: 'carcass',
          });
          hardware.push({
            id: `${cabinetId}/shelf-support-${ci + 1}-${s + 1}`,
            cabinetId,
            kind: 'shelf-support',
            label: 'Nosač police (generički)',
            quantity: 4,
            generic: true,
          });
        }
      });
    }
  } else if (p.shelves.count > 0 && hasDrawers) {
    issues.push({ severity: 'info', code: 'shelves.ignored', message: 'Police se ne generišu u elementu sa ladicama.', cabinetId });
  }

  // --- Plinth ---
  if (p.plinthHeight > 0) {
    add({
      localKey: 'plinth-front',
      role: 'plinth',
      label: 'Sokla',
      box: { min: [0, 0, frontZ - p.plinthSetback - p.sideThickness], size: [W, p.plinthHeight, p.sideThickness] },
      lengthAxis: 'x',
      widthAxis: 'y',
      materialSlot: 'plinth',
    });
  }
  if (p.legsHeight > 0) {
    hardware.push({
      id: `${cabinetId}/legs`,
      cabinetId,
      kind: 'leg',
      label: `Nogica ${p.legsHeight / 1000} mm (generička)`,
      quantity: W > mm(900) ? 6 : 4,
      generic: true,
      note: 'Broj nogica je generička procjena bez proračuna nosivosti.',
    });
  }

  // --- Fronts ---
  buildFronts(cabinetId, p, frame, add, hardware, issues);

  if (issues.some((i) => i.severity === 'error')) {
    return { cabinetId, parts: [], hardware: [], issues, bounds: { size: [0, 0, 0] } };
  }

  const parts = specs.map(makePanel);
  annotateConnections(parts);
  issues.push(...detectCollisions(cabinetId, parts));

  const frontDepth = p.fronts.rows.length > 0 && p.fronts.mounting === 'overlay' ? p.frontThickness : 0;
  const size: Vec3 = [W, base + H, p.depth + frontDepth];
  return { cabinetId, parts, hardware, issues, bounds: { size } };
}

function splitCompartments(f: Frame, p: CabinetParams): { x0: Um; x1: Um }[] {
  const n = p.dividers + 1;
  const clear = f.innerX1 - f.innerX0 - p.dividers * p.dividerThickness;
  const out: { x0: Um; x1: Um }[] = [];
  let x = f.innerX0;
  for (let i = 0; i < n; i++) {
    const end = f.innerX0 + Math.round((clear * (i + 1)) / n) + i * p.dividerThickness;
    out.push({ x0: x, x1: end });
    x = end + p.dividerThickness;
  }
  return out;
}

function resolveRowHeights(rows: FrontRow[], available: Um): Um[] | null {
  const fixed = rows.reduce((s, r) => s + (r.height ?? 0), 0);
  const flex = rows.filter((r) => r.height === null).length;
  const remaining = available - fixed;
  if (flex === 0) return remaining === 0 ? rows.map((r) => r.height!) : null;
  if (remaining < flex * MIN_FRONT_HEIGHT) return null;
  const out: Um[] = [];
  let used = 0;
  let flexSeen = 0;
  for (const r of rows) {
    if (r.height !== null) out.push(r.height);
    else {
      flexSeen++;
      const end = Math.round((remaining * flexSeen) / flex);
      out.push(end - used);
      used = end;
    }
  }
  return out;
}

function buildFronts(
  cabinetId: string,
  p: CabinetParams,
  f: Frame,
  add: (s: Omit<PanelSpec, 'cabinetId'>) => void,
  hardware: HardwareItem[],
  issues: Issue[],
): void {
  const rows = p.fronts.rows;
  if (rows.length === 0) return;
  const g = p.fronts.gapOuter;
  const gb = p.fronts.gapBetween;
  const inset = p.fronts.mounting === 'inset';
  const x0 = inset ? f.innerX0 + g : g;
  const x1 = inset ? f.innerX1 - g : p.width - g;
  const yBottom = inset ? f.innerY0 + g : f.y0 + g;
  const yTop = inset ? f.innerY1 - g : f.y0 + p.height - g;
  const z = inset ? f.frontZ - p.frontThickness : f.frontZ;
  const frontW = x1 - x0;
  const available = yTop - yBottom - gb * (rows.length - 1);
  if (frontW < MIN_FRONT_HEIGHT) {
    issues.push({ severity: 'error', code: 'fronts.too-narrow', message: 'Fronta je preuska.', cabinetId, param: 'fronts' });
    return;
  }
  const heights = resolveRowHeights(rows, available);
  if (!heights) {
    issues.push({
      severity: 'error',
      code: 'fronts.heights',
      message: `Visine fronti ne odgovaraju raspoloživoj visini ${available / 1000} mm.`,
      cabinetId,
      param: 'fronts.rows',
    });
    return;
  }
  let yCursor = yTop;
  let drawerIndex = 0;
  let doorIndex = 0;
  rows.forEach((row, ri) => {
    const h = heights[ri]!;
    const yMin = yCursor - h;
    yCursor = yMin - gb;
    if (row.kind === 'open') return;
    if (row.kind === 'drawer') {
      drawerIndex++;
      add({
        localKey: `drawer-${drawerIndex}-front`,
        role: 'drawer-front',
        label: `Front ladice ${drawerIndex}`,
        box: { min: [x0, yMin, z], size: [frontW, h, p.frontThickness] },
        lengthAxis: 'x',
        widthAxis: 'y',
        materialSlot: 'front',
        grain: 'length',
        movable: 'slide',
      });
      buildDrawerBox(cabinetId, p, f, drawerIndex, yMin, h, add, issues);
      hardware.push({
        id: `${cabinetId}/drawer-${drawerIndex}-slides`,
        cabinetId,
        kind: 'slide',
        label: p.drawerSystem.label,
        quantity: 1,
        generic: true,
        note: 'Par vodilica; bočni razmak je ručno zadat, bez CNC tvrdnje.',
      });
      return;
    }
    const leaves: { key: string; x: Um; w: Um; hinge: 'hinge-left' | 'hinge-right' }[] = [];
    if (row.kind === 'double-door') {
      const left = Math.floor((frontW - gb) / 2);
      const right = frontW - gb - left;
      doorIndex++;
      leaves.push({ key: `door-${doorIndex}-left`, x: x0, w: left, hinge: 'hinge-left' });
      leaves.push({ key: `door-${doorIndex}-right`, x: x0 + left + gb, w: right, hinge: 'hinge-right' });
    } else {
      doorIndex++;
      leaves.push({ key: `door-${doorIndex}`, x: x0, w: frontW, hinge: row.kind === 'door-left' ? 'hinge-left' : 'hinge-right' });
    }
    for (const leaf of leaves) {
      add({
        localKey: leaf.key,
        role: 'door',
        label: leaves.length > 1 ? (leaf.hinge === 'hinge-left' ? 'Vrata lijeva' : 'Vrata desna') : 'Vrata',
        box: { min: [leaf.x, yMin, z], size: [leaf.w, h, p.frontThickness] },
        lengthAxis: 'y',
        widthAxis: 'x',
        materialSlot: 'front',
        grain: 'length',
        movable: leaf.hinge,
      });
      hardware.push({
        id: `${cabinetId}/${leaf.key}-hinges`,
        cabinetId,
        kind: 'hinge',
        label: 'Šarka (generička)',
        quantity: 0,
        generic: true,
        note: 'Broj šarki zahtijeva model šarke i masu vrata — nije izračunat.',
      });
    }
  });
}

function buildDrawerBox(
  cabinetId: string,
  p: CabinetParams,
  f: Frame,
  n: number,
  frontY: Um,
  frontH: Um,
  add: (s: Omit<PanelSpec, 'cabinetId'>) => void,
  issues: Issue[],
): void {
  const ds = p.drawerSystem;
  const inset = p.fronts.mounting === 'inset' ? p.frontThickness : 0;
  const innerDepth = f.frontZ - inset - f.innerZ0;
  if (ds.nominalLength > innerDepth) {
    issues.push({
      severity: 'error',
      code: 'drawer.too-long',
      message: `Vodilica ${ds.nominalLength / 1000} mm ne staje u unutrašnju dubinu ${innerDepth / 1000} mm.`,
      cabinetId,
      param: 'drawerSystem.nominalLength',
    });
    return;
  }
  if (p.dividers > 0) {
    issues.push({ severity: 'warning', code: 'drawer.dividers', message: 'Ladice sa pregradama: kutija se računa preko cijele širine.', cabinetId });
  }
  const boxW = f.innerX1 - f.innerX0 - 2 * ds.sideClearance;
  const boxH = frontH - ds.boxHeightReduction;
  if (boxW < MIN_PANEL * 4 || boxH < MIN_PANEL * 2) {
    issues.push({ severity: 'error', code: 'drawer.too-small', message: `Ladica ${n} je premala.`, cabinetId });
    return;
  }
  const x0 = f.innerX0 + ds.sideClearance;
  const y0 = Math.max(frontY, f.innerY0) + mm(10);
  const zFront = f.frontZ - inset;
  const z0 = zFront - ds.nominalLength;
  const t = ds.boxSideThickness;
  const bt = ds.boxBottomThickness;
  const common = { materialSlot: 'drawer-box' as const, generic: true, movable: 'slide' as const };
  add({ ...common, localKey: `drawer-${n}-side-left`, role: 'drawer-side', label: `Ladica ${n} bok L`, box: { min: [x0, y0 + bt, z0], size: [t, boxH - bt, ds.nominalLength] }, lengthAxis: 'z', widthAxis: 'y' });
  add({ ...common, localKey: `drawer-${n}-side-right`, role: 'drawer-side', label: `Ladica ${n} bok D`, box: { min: [x0 + boxW - t, y0 + bt, z0], size: [t, boxH - bt, ds.nominalLength] }, lengthAxis: 'z', widthAxis: 'y' });
  add({ ...common, localKey: `drawer-${n}-back`, role: 'drawer-back', label: `Ladica ${n} zadnja`, box: { min: [x0 + t, y0 + bt, z0], size: [boxW - 2 * t, boxH - bt, t] }, lengthAxis: 'x', widthAxis: 'y' });
  add({ ...common, localKey: `drawer-${n}-inner-front`, role: 'drawer-back', label: `Ladica ${n} unutrašnja čeona`, box: { min: [x0 + t, y0 + bt, zFront - t], size: [boxW - 2 * t, boxH - bt, t] }, lengthAxis: 'x', widthAxis: 'y' });
  add({ ...common, localKey: `drawer-${n}-bottom`, role: 'drawer-bottom', label: `Ladica ${n} dno`, materialSlot: 'drawer-bottom', grain: 'none', box: { min: [x0, y0, z0], size: [boxW, bt, ds.nominalLength] }, lengthAxis: 'x', widthAxis: 'z' });
}

function annotateConnections(parts: PanelPart[]): void {
  const byId = new Map(parts.map((p) => [p.id, p]));
  for (const cov of computeEdgeCoverage(parts, MAX_ENCLOSED_GAP)) {
    const part = byId.get(cov.partId)!;
    part.edges[cov.edge].contacts = cov.contacts;
    for (const c of cov.contacts) {
      if (c.gap <= TOLERANCE.contact) part.connections.push({ partId: c.partId, kind: 'butt', edge: cov.edge });
    }
  }
  const back = parts.find((p) => p.localKey === 'back' && p.label.includes('žlijeb'));
  if (back) {
    for (const p of parts) {
      if (['side', 'bottom', 'top'].includes(p.role) && boxPenetration(partBox(p), partBox(back)) > 0) {
        p.connections.push({ partId: back.id, kind: 'groove' });
        back.connections.push({ partId: p.id, kind: 'groove' });
      }
    }
  }
}

export function detectCollisions(cabinetId: string, parts: PanelPart[]): Issue[] {
  const issues: Issue[] = [];
  const boxes = parts.map((p) => partBox(p));
  for (let i = 0; i < parts.length; i++) {
    for (let j = i + 1; j < parts.length; j++) {
      const a = parts[i]!;
      const b = parts[j]!;
      if (a.connections.some((c) => c.partId === b.id && (c.kind === 'groove' || c.kind === 'rebate'))) continue;
      const d = boxPenetration(boxes[i]!, boxes[j]!);
      if (d > TOLERANCE.collision) {
        issues.push({
          severity: 'error',
          code: 'collision.static',
          message: `Kolizija: ${a.label} i ${b.label} (${d / 1000} mm).`,
          cabinetId,
          partId: a.id,
        });
      }
    }
  }
  return issues;
}
